import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton, {
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ArrowIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-right';
import BpkImage, {
  withLazyLoading,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import type { EditorialCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/EditorialCardProps';

import STYLES from './EditorialCard.module.scss';

// Support for SSR
const documentIfExists = typeof window !== 'undefined' ? document : null;
// withLazyLoading will not use document when rendered server side so it's safe
// pass null.
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);

const RtlArrowIcon = withRtlSupport(ArrowIcon);

const DESKTOP_IMAGE_RATIO = 600 / 460;
const MOBILE_IMAGE_RATIO = 329 / 218;

const EditorialCard = ({
  body,
  buttonLabel,
  href,
  image,
  subtitle,
  title,
}: EditorialCardProps) => {
  const isLinkable = !!href;
  const hasButton = href && buttonLabel;
  const hasBodySection = body || hasButton;

  return (
    <BpkBreakpoint query={BREAKPOINTS.SMALL_TABLET} matchSSR>
      {(isMobile: boolean) => (
        <div
          data-testid="editorial-card"
          className={[
            STYLES.EditorialCard,
            isLinkable && STYLES['EditorialCard--with-link'],
          ].join(' ')}
        >
          {isLinkable && (
            <a
              href={href}
              className={STYLES.Link}
              aria-label={buttonLabel || title}
            />
          )}
          <div className={STYLES.Image}>
            <LazyLoadedImage
              aspectRatio={isMobile ? MOBILE_IMAGE_RATIO : DESKTOP_IMAGE_RATIO}
              altText={image.altText || ''}
              src={image.src}
            />
          </div>
          <div className={STYLES.ContentSection}>
            <div className={STYLES.TitlesSection}>
              <BpkText
                tagName="h3"
                textStyle={
                  isMobile ? TEXT_STYLES.heading2 : TEXT_STYLES.heading1
                }
              >
                {title}
              </BpkText>

              {subtitle && (
                <BpkText tagName="h4" textStyle={TEXT_STYLES.editorial3}>
                  {subtitle}
                </BpkText>
              )}
            </div>
            {hasBodySection && (
              <div className={STYLES.BodySection}>
                {body && (
                  <div className={STYLES.Body}>
                    <BpkText
                      tagName="p"
                      textStyle={
                        isMobile
                          ? TEXT_STYLES.bodyDefault
                          : TEXT_STYLES.bodyLongform
                      }
                    >
                      {body}
                    </BpkText>
                  </div>
                )}
                {hasButton && (
                  <div className={STYLES.Cta}>
                    <BpkButton
                      type={BUTTON_TYPES.link}
                      size={SIZE_TYPES.large}
                      href={href}
                      implicit
                    >
                      {buttonLabel} <RtlArrowIcon />
                    </BpkButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </BpkBreakpoint>
  );
};

export default EditorialCard;
